<template>
  <div
    class="apk"
    @dragover.prevent="dragover"
    @dragleave.prevent="dragleave"
    @drop.prevent="drop"
    @paste="onPaste"
  >
    <div v-if="documentsUrl.length > 0" class="apk-media">
      <div
        v-for="(doc, i) in documentsUrl"
        :key="doc.fileId || i"
        class="apk-media-item"
        role="button"
        tabindex="0"
        @click="openDocument(doc)"
        @keydown.enter="openDocument(doc)"
      >
        <img
          v-if="doc.fileType === 'image'"
          :src="doc.url"
          :alt="doc.fileName"
          class="apk-media-thumb"
          loading="lazy"
        />
        <video
          v-if="doc.fileType === 'video'"
          :src="doc.url"
          class="apk-media-thumb"
          muted
          preload="metadata"
        ></video>
          <audio controls
          v-else-if="doc.fileType === 'audio'"
          
          class="apk-media-thumb"
          muted
          preload="metadata"
        ><source :src="doc.url" :type="doc.contentType" /></audio>
        <div v-else class="apk-media-file">
          <span v-html="getFileMimeType(doc).icon"></span>
          <span class="apk-media-name">{{ doc.fileName }}</span>
        </div>
        <div class="apk-media-actions">
          <f7-link
            v-if="doc.fileType === 'image'"
            icon-f7="pencil_circle"
            icon-size="18"
            color="blue"
            @click.stop="editImage(doc)"
          ></f7-link>
          <f7-link
            icon-f7="xmark_circle"
            icon-size="18"
            color="red"
            @click.stop="removeDoc(i)"
          ></f7-link>
        </div>
      </div>
    </div>

    <f7-button
      type="button"
      class="apk-dropzone attachFileButton"
      fill
      raised
      @click="attachFileOpenActions({ takePhoto, openPreviewCamera })"
    >
      <f7-icon
        f7="photo_on_rectangle"
        :size="20"
        color="var(--avocash-green)"
      />
      <span class="apk-dropzone-title">{{ $t("common.uploadFiles") }}</span>
      <span class="apk-dropzone-hint">{{ $t("common.dropFilesHere") }}</span>
    </f7-button>

    <div v-if="isDragging" class="apk-overlay">
      <f7-icon f7="square_arrow_down" size="30" color="var(--avocash-green)" />
      <span>{{ $t("common.dropFilesHere") }}</span>
    </div>
  </div>
</template>

<script setup>
import { useAttachDocs } from "../../js/composables/useAttachDocs.js";
import { useCamera } from "../../js/composables/useCamera.js";

const {
  documentsUrl,
  attachFileOpenActions,
  removeDoc,
  editImage,
  openDocument,
  getFileMimeType,
  isDragging,
  onPaste,
  dragover,
  dragleave,
  drop,
} = useAttachDocs({});
const { takePhoto, openPreviewCamera } = useCamera();
</script>

<style scoped>
.apk {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 2px 0;
}

.apk-media {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.apk-media-item {
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(28, 28, 30, 0.08);
  background: var(--f7-list-bg-color, #f9f9f9);
  flex-shrink: 0;
}

.apk-media-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.apk-media-file {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px;
  text-align: center;
}

.apk-media-name {
  font-size: 9px;
  line-height: 1.2;
  color: var(--f7-on-surface-variant, #8e8e93);
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.apk-media-actions {
  position: absolute;
  top: 2px;
  right: 2px;
  display: flex;
  gap: 2px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 999px;
  padding: 0 2px;
}

.apk-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 140px;
  padding: 14px;
  border: 1.5px dashed rgba(26, 107, 66, 0.45);
  border-radius: 14px;
  background: rgba(26, 107, 66, 0.03);
  font-size: 13px;
  font-weight: 600;
  color: var(--avocash-green, #1a6b42);
  --f7-button-bg-color: transparent;
  --f7-button-border-color: rgba(26, 107, 66, 0.45);
  --f7-button-border-width: 1.5px;
  --f7-button-border-style: dashed;
  --f7-button-border-radius: 14px;
  --f7-button-text-color: var(--avocash-green, #1a6b42);
  --f7-button-font-size: 13px;
  --f7-button-font-weight: 600;
}
.apk-dropzone:hover {
  background: rgba(26, 107, 66, 0.07);
  border-color: var(--avocash-green, #1a6b42);
}

.apk-dropzone-title {
  font-size: 14px;
  font-weight: 700;
}

.apk-dropzone-hint {
  font-size: 12px;
  font-weight: 500;
  color: #5f6b66;
}

.apk-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(26, 107, 66, 0.08);
  border: 2px dashed var(--avocash-green, #1a6b42);
  border-radius: 14px;
  font-size: 13px;
  font-weight: 600;
  color: var(--avocash-green, #1a6b42);
  pointer-events: none;
}
</style>
